/**
 * Firebase実装のノードリポジトリ
 */

import { v4 as uuidv4 } from 'uuid';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { INodeRepository } from '@/repositories/interfaces';
import { TopicNode } from '@/types/nodeTypes';
import { getFirestoreInstance } from '@/lib/firebase';
import { FirebaseMapRepository } from './FirebaseMapRepository';

/**
 * Firebase実装のノードリポジトリクラス
 */
export class FirebaseNodeRepository implements INodeRepository {
  private db = getFirestoreInstance();
  
  /**
   * コンストラクタ
   * @param mapRepository マップリポジトリの参照
   */
  constructor(private mapRepository: FirebaseMapRepository) {}

  /**
   * 指定IDのノードを取得
   */
  public async getNode(mapId: string, nodeId: string): Promise<TopicNode | null> {
    try {
      // マップからノードを取得
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        return null;
      }

      const node = map.nodes.find(n => n.id === nodeId);
      return node ? { ...node } : null;
    } catch (error) {
      console.error('Error getting node:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトのノードを取得
   */
  public async getUserProjectNode(userId: string, projectId: string, nodeId: string): Promise<TopicNode | null> {
    try {
      const nodeDoc = await getDoc(doc(this.db, `users/${userId}/projects/${projectId}/nodes`, nodeId));
      
      if (nodeDoc.exists()) {
        return { ...nodeDoc.data(), id: nodeDoc.id } as TopicNode;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user project node:', error);
      throw error;
    }
  }

  /**
   * 指定マップの全ノードを取得
   */
  public async getNodes(mapId: string): Promise<TopicNode[]> {
    try {
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        return [];
      }

      return map.nodes.map(node => ({ ...node }));
    } catch (error) {
      console.error('Error getting nodes:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトの全ノードを取得
   */
  public async getUserProjectNodes(userId: string, projectId: string): Promise<TopicNode[]> {
    try {
      const nodesSnapshot = await getDocs(collection(this.db, `users/${userId}/projects/${projectId}/nodes`));
      
      return nodesSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as TopicNode));
    } catch (error) {
      console.error('Error getting user project nodes:', error);
      throw error;
    }
  }

  /**
   * ノードを更新
   */
  public async updateNode(
    mapId: string,
    nodeId: string,
    update: Partial<TopicNode>
  ): Promise<TopicNode | null> {
    try {
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        return null;
      }

      const nodeIndex = map.nodes.findIndex(n => n.id === nodeId);
      if (nodeIndex === -1) {
        return null;
      }

      const updatedNode = {
        ...map.nodes[nodeIndex],
        ...update
      };

      const updatedNodes = [
        ...map.nodes.slice(0, nodeIndex),
        updatedNode,
        ...map.nodes.slice(nodeIndex + 1)
      ];

      await this.mapRepository.updateMap(mapId, { nodes: updatedNodes });
      return { ...updatedNode };
    } catch (error) {
      console.error('Error updating node:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトのノードを更新
   */
  public async updateUserProjectNode(
    userId: string,
    projectId: string,
    nodeId: string,
    update: Partial<TopicNode>
  ): Promise<TopicNode | null> {
    try {
      const nodeRef = doc(this.db, `users/${userId}/projects/${projectId}/nodes`, nodeId);
      const nodeDoc = await getDoc(nodeRef);
      
      if (!nodeDoc.exists()) {
        return null;
      }
      
      const updatedNode = {
        ...nodeDoc.data(),
        ...update
      };
      
      await updateDoc(nodeRef, updatedNode);
      
      return { ...updatedNode, id: nodeId } as TopicNode;
    } catch (error) {
      console.error('Error updating user project node:', error);
      throw error;
    }
  }

  /**
   * 新しいノードを作成
   */
  public async createNode(mapId: string, node: Omit<TopicNode, 'id'>): Promise<TopicNode> {
    try {
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        throw new Error(`Map with ID ${mapId} not found`);
      }

      const newNode: TopicNode = {
        ...node,
        id: uuidv4()
      };

      const updatedNodes = [...map.nodes, newNode];
      await this.mapRepository.updateMap(mapId, { nodes: updatedNodes });

      return { ...newNode };
    } catch (error) {
      console.error('Error creating node:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトに新しいノードを作成
   */
  public async createUserProjectNode(
    userId: string,
    projectId: string,
    node: Omit<TopicNode, 'id'>
  ): Promise<TopicNode> {
    try {
      const nodeId = uuidv4();
      const nodeRef = doc(this.db, `users/${userId}/projects/${projectId}/nodes`, nodeId);
      
      await setDoc(nodeRef, {
        ...node,
        id: nodeId
      });
      
      return { ...node, id: nodeId } as TopicNode;
    } catch (error) {
      console.error('Error creating user project node:', error);
      throw error;
    }
  }

  /**
   * ノードを削除
   */
  public async deleteNode(mapId: string, nodeId: string): Promise<boolean> {
    try {
      const map = await this.mapRepository.getMap(mapId);
      if (!map) {
        return false;
      }

      const nodeIndex = map.nodes.findIndex(n => n.id === nodeId);
      if (nodeIndex === -1) {
        return false;
      }

      const updatedNodes = [
        ...map.nodes.slice(0, nodeIndex),
        ...map.nodes.slice(nodeIndex + 1)
      ];

      await this.mapRepository.updateMap(mapId, { nodes: updatedNodes });
      return true;
    } catch (error) {
      console.error('Error deleting node:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトのノードを削除
   */
  public async deleteUserProjectNode(
    userId: string,
    projectId: string,
    nodeId: string
  ): Promise<boolean> {
    try {
      const nodeRef = doc(this.db, `users/${userId}/projects/${projectId}/nodes`, nodeId);
      
      await deleteDoc(nodeRef);
      return true;
    } catch (error) {
      console.error('Error deleting user project node:', error);
      throw error;
    }
  }

  /**
   * ノードの位置を更新
   */
  public async updateNodePosition(
    mapId: string,
    nodeId: string,
    position: { x: number; y: number }
  ): Promise<boolean> {
    try {
      const node = await this.getNode(mapId, nodeId);
      if (!node) {
        return false;
      }

      await this.updateNode(mapId, nodeId, { position });
      return true;
    } catch (error) {
      console.error('Error updating node position:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトのノード位置を更新
   */
  public async updateUserProjectNodePosition(
    userId: string,
    projectId: string,
    nodeId: string,
    position: { x: number; y: number }
  ): Promise<boolean> {
    try {
      const nodeRef = doc(this.db, `users/${userId}/projects/${projectId}/nodes`, nodeId);
      
      await updateDoc(nodeRef, { position });
      return true;
    } catch (error) {
      console.error('Error updating user project node position:', error);
      throw error;
    }
  }

  /**
   * ノードのステータスを更新
   */
  public async updateNodeStatus(
    mapId: string,
    nodeId: string,
    status: TopicNode['status']
  ): Promise<boolean> {
    try {
      const node = await this.getNode(mapId, nodeId);
      if (!node) {
        return false;
      }

      await this.updateNode(mapId, nodeId, { status });
      return true;
    } catch (error) {
      console.error('Error updating node status:', error);
      throw error;
    }
  }

  /**
   * ユーザープロジェクトのノードステータスを更新
   */
  public async updateUserProjectNodeStatus(
    userId: string,
    projectId: string,
    nodeId: string,
    status: TopicNode['status']
  ): Promise<boolean> {
    try {
      const nodeRef = doc(this.db, `users/${userId}/projects/${projectId}/nodes`, nodeId);
      
      await updateDoc(nodeRef, { status });
      return true;
    } catch (error) {
      console.error('Error updating user project node status:', error);
      throw error;
    }
  }

  /**
   * 複数のノードを一括で更新（バッチ処理）
   */
  public async bulkUpdateUserProjectNodes(
    userId: string,
    projectId: string,
    updates: { id: string; update: Partial<TopicNode> }[]
  ): Promise<boolean> {
    try {
      const batch = writeBatch(this.db);
      
      for (const { id, update } of updates) {
        const nodeRef = doc(this.db, `users/${userId}/projects/${projectId}/nodes`, id);
        batch.update(nodeRef, update);
      }
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error bulk updating user project nodes:', error);
      throw error;
    }
  }
}